import React, { useMemo } from 'react';
import { Button } from 'react-aria-components';
import { SearchFacet } from '../../../types/search';
import PortalMonthlyDateSelector from './PortalMonthlyDateSelector';
import PortalDateRangeSelector from './PortalDateRangeSelector';
import { useDateFilter } from '../contexts/PortalDateFilterProvider';
import { useCalendarViewMode } from '../hooks/useCalendarViewMode';

interface PortalDateTypeSelectorProps {
  readonly currentDateFacet: SearchFacet;
}

export default function PortalDateTypeSelector({ currentDateFacet }: PortalDateTypeSelectorProps) {
  const { selectedValue, selectedValueLabel, setSelectedValue, setSelectedValueLabel } = useDateFilter(
    currentDateFacet.key
  );

  const dateTypes = useMemo(() => {
    // Backend sends lowercase keys: datefrom, dateto
    if (currentDateFacet.key === 'datefrom' || currentDateFacet.key === 'dateto') {
      return [
        { key: 'monthly', label: 'Monthly' },
        { key: 'custom', label: 'Custom' },
      ];
    }
    return [
      { key: 'weekly', label: 'Weekly' },
      { key: 'monthly', label: 'Monthly' },
      { key: 'custom', label: 'Custom' },
    ];
  }, [currentDateFacet.key]);

  // Determine initial date type based on existing selection
  const mode = useCalendarViewMode(selectedValue);
  const initialDateType = useMemo(() => {
    const modeKeyMap: Record<string, string> = {
      Weekly: 'weekly',
      Monthly: 'monthly',
      Custom: 'custom',
    };
    const key = modeKeyMap[mode];
    // Find the matching date type, or fall back to first available
    const matchingType = dateTypes.find(dt => dt.key === key);
    return matchingType || dateTypes[0];
  }, [mode, dateTypes]);

  const [selectedDateType, setSelectedDateType] = React.useState<Record<string, string>>(initialDateType);

  function handleTabClick(key: string, label: string) {
    setSelectedValue('');
    setSelectedValueLabel('');
    setSelectedDateType({ key, label });
  }

  return (
    <>
      <div className="portal-date-type-selector__tabs">
        {selectedDateType.key === 'custom' && (
          <div className="portal-date-type-selector__warning">
            <svg className="portal-date-type-selector__warning-icon icon" aria-hidden="true">
              <use href="/dist/assets/icons/sprite.svg#warning"></use>
            </svg>
            <p className="portal-date-type-selector__warning-text">You can only select up to 3 months.</p>
          </div>
        )}
        {dateTypes.map(({ key, label }) => {
          const isSelected = selectedDateType.key === key;

          return (
            <Button
              key={key}
              value={key}
              className={`portal-date-type-selector__tab ${isSelected ? 'portal-date-type-selector__tab--selected' : ''}`}
              autoFocus={isSelected}
              aria-selected={isSelected}
              onPress={() => handleTabClick(key, label)}
              type="button"
            >
              {label}
            </Button>
          );
        })}
      </div>
      <div className="portal-date-type-selector__selected-label">{selectedValueLabel}</div>
      <div className="portal-date-type-selector__tab-container">
        {selectedDateType.key === 'weekly' ? (
          <PortalDateRangeSelector selectionType="weekly" currentDateFacet={currentDateFacet} />
        ) : null}
        {selectedDateType.key === 'monthly' ? <PortalMonthlyDateSelector currentDateFacet={currentDateFacet} /> : null}
        {selectedDateType.key === 'custom' ? (
          <PortalDateRangeSelector selectionType="custom" currentDateFacet={currentDateFacet} />
        ) : null}
      </div>
    </>
  );
}
